import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const DEFAULT_STAGES = [
  { id: 1, name: 'Prospecting' },
  { id: 2, name: 'Qualification' },
  { id: 3, name: 'Proposal' },
  { id: 4, name: 'Negotiation' },
];

/**
 * Sprint 15 QA — Convert Contact → Deal. Creates a deal via
 * POST /api/contacts/:id/convert (409 when already converted). Stage list
 * comes from the org pipeline config; terminal (won/lost) stages are excluded
 * because a deal should start open.
 */
export default function ConvertDealModal({ isOpen, contact, stages, onClose, onSubmit }) {
  const [formData, setFormData] = useState({ name: '', amount: '', stage: 1 });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: contact?.name || '',
        amount: '',
        stage: stages?.find((s) => !s.isClosed)?.id ?? 1,
      });
      setErrors({});
    }
  }, [isOpen, contact, stages]);

  const openStages = (Array.isArray(stages) && stages.length ? stages : DEFAULT_STAGES).filter((s) => !s.isClosed);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Deal name is required';
    if (formData.amount && Number(formData.amount) < 0) newErrors.amount = 'Amount cannot be negative';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setSaving(true);
    try {
      await onSubmit({
        name: formData.name.trim(),
        amount: formData.amount ? Number(formData.amount) : undefined,
        stage: Number(formData.stage || 1),
      });
    } catch {
      // Parent surfaces the error; the modal stays open for retry.
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
        <h2 className="text-2xl font-bold mb-1 text-gray-800">Convert to Deal</h2>
        <p className="text-sm text-gray-500 mb-4">
          Create an opportunity from <b>{contact?.name}</b>. This links the contact to the new deal.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Deal Name *</label>
            <Input
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Deal name"
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Deal Value (₹)</label>
            <Input
              name="amount"
              type="number"
              min="0"
              value={formData.amount}
              onChange={handleChange}
              placeholder="e.g., 50000"
              className={errors.amount ? 'border-red-500' : ''}
            />
            {errors.amount && <p className="text-red-500 text-sm mt-1">{errors.amount}</p>}
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Stage</label>
            <Select
              name="stage"
              value={String(formData.stage)}
              onChange={(value) => {
                setFormData((prev) => ({ ...prev, stage: Number(value) }));
                if (errors.stage) setErrors((prev) => ({ ...prev, stage: "" }));
              }}
              options={openStages.map((s) => ({ value: String(s.id), label: s.name }))}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              onClick={onClose}
              disabled={saving}
              className="flex-1 bg-gray-200 text-gray-800 hover:bg-gray-300"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={saving}
              className="flex-1 bg-blue-600 text-white hover:bg-blue-700"
            >
              {saving ? 'Converting…' : 'Convert to Deal'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}